import {
  QueryClient,
  useMutation,
  type UseMutationOptions,
} from '@tanstack/react-query'
import { produce } from 'immer'
import {
  moments,
  type OpalApiRequestBody,
  type OpalApiResponseDocument,
} from '@opallabs/api/v3'
import {
  type ReadableAccessMetadata,
  type WritableAccessMetadata,
} from '@opallabs/concepts'
import { queryKeys as planQueryKeys } from '@opallabs/plans/src/queries/queryKeys'
import { globalQueryKeys } from '../../queryKeys'

type UpdateOperation = 'UpdateMomentV3'
export type ResponseBody = OpalApiResponseDocument<UpdateOperation, 200>

type RequestData = OpalApiRequestBody<UpdateOperation>['data']
export type MutationParams = RequestData['attributes'] & {
  access: WritableAccessMetadata
  id: RequestData['id']
}

type HookOptions = Pick<
  UseMutationOptions<ResponseBody, unknown, MutationParams>,
  'onMutate' | 'onSuccess'
>

export function transformMomentUsingRequest({
  access,
  cachedGroupsData,
  cachedMomentData,
}: {
  access: WritableAccessMetadata
  cachedGroupsData?: OpalApiResponseDocument<'ReadUserGroupsV3', 200>['data']
  cachedMomentData?: ResponseBody
}) {
  if (!cachedMomentData) return

  return produce(cachedMomentData, (draftDocument) => {
    if (draftDocument?.meta?.access) {
      draftDocument.meta.access.users = access.users ?? []

      draftDocument.meta.access.groups =
        access.groups?.reduce<ReadableAccessMetadata['groups']>(
          (groups, group) => {
            const cachedGroup = cachedGroupsData?.find((g) => g.id === group.id)
            if (cachedGroup) {
              groups.push({
                type: 'user_group',
                id: group.id,
                meta: {
                  title: cachedGroup.attributes.name,
                  count: cachedGroup.relationships.members.data.length,
                },
              })
            }

            return groups
          },
          []
        ) ?? []

      draftDocument.meta.access.resources.forEach((resource) => {
        const inheritsMembership = access.resources?.find(
          (r) => r.id === resource.id
        )?.meta.inherits_membership

        if (inheritsMembership) {
          resource.meta.inherits_membership = inheritsMembership
        }
      })
    }

    return draftDocument
  })
}

/**
 * Call this within `useUpdateMomentAccess`’s `onMutate` prop to update the `react-query` cache for a given moment.
 * Should be used together with `updateMomentForPersistedAccess` to update the cache to the final state.
 *
 * @example
 * const queryClient = useQueryClient()
 * // ...
 * useUpdateMomentAccess({
 *   onMutate: (mutationParams) => {
 *     optimisticallyUpdateMomentForAccess(queryClient, mutationParams)
 *   },
 * })
 */
export function optimisticallyUpdateMomentForAccess(
  queryClient: QueryClient,
  params: MutationParams
) {
  const { id, access } = params
  const { data: groupData } =
    queryClient.getQueryData<OpalApiResponseDocument<'ReadUserGroupsV3', 200>>(
      globalQueryKeys.groups(undefined, ['asset', 'members'])
    ) ?? {}

  queryClient.setQueryData<ResponseBody>(
    globalQueryKeys.momentAccess(id),
    (cachedData) =>
      transformMomentUsingRequest({
        access,
        cachedGroupsData: groupData,
        cachedMomentData: cachedData,
      })
  )
}

/** @private Only exported for tests */
export function transformMomentUsingResponse(
  response: ResponseBody,
  cachedMoment?: ResponseBody
): ResponseBody {
  if (!cachedMoment) return response

  return produce(cachedMoment, (draftDocument) => {
    if (draftDocument?.meta?.access) {
      draftDocument.meta.access = response.meta?.access
    }
    if (draftDocument?.data?.meta?.effective_privacy) {
      draftDocument.data.meta.effective_privacy =
        response.data.meta?.effective_privacy
    }

    return draftDocument
  }) satisfies ResponseBody
}

/**
 * Call this within `useUpdateMomentAccess`’s `onSuccess` prop to update the `react-query` cache for a given moment.
 *
 * @example
 * const queryClient = useQueryClient()
 * // ...
 * useUpdateMomentAccess({
 *   onSuccess: (data) => {
 *     updateMomentForPersistedAccess(queryClient, data)
 *   },
 * })
 */
export function updateMomentForPersistedAccess(
  queryClient: QueryClient,
  response: ResponseBody
) {
  const id = response.data.id
  const queryKey = globalQueryKeys.momentAccess(id)

  // If we have the meta.access structure in the response,
  // use it to update the cache instead of invalidating
  if (response.meta?.access != null) {
    queryClient.setQueryData<ResponseBody>(queryKey, (cachedData) =>
      transformMomentUsingResponse(response, cachedData)
    )
  } else {
    queryClient.invalidateQueries(queryKey)
  }

  // Paired block access should also be invalidated
  if (response.data.relationships.paired_block?.data != null) {
    const pairedBlockQueryKey = planQueryKeys.blockAccess({
      blockId: response.data.relationships.paired_block.data.id,
      workspaceId: response.data.relationships.workspace.data?.id ?? '',
    })
    queryClient.invalidateQueries(pairedBlockQueryKey)
  }
}

export const useUpdateMomentAccess = ({ onMutate, onSuccess }: HookOptions) =>
  useMutation({
    mutationFn: async (params) => {
      const { access, id } = params

      return await moments.update({
        params: {
          path: {
            moment_id: id,
          },
          query: {
            expose: {
              moment: {
                meta: 'effective_privacy',
              },
              meta: {
                access: 'true',
              },
            },
          },
        },
        requestBody: {
          data: {
            id,
            type: 'moment',
          },
          meta: {
            access,
          },
        },
      })
    },
    onMutate,
    onSuccess,
  })
